import { parseLocale } from '@tesla/parse-locale';
/**
 * Connects the header to it's spot in localStorage
 * Key is used for lookup and setting purposes
 * To verify localStorage for testing
 * >> localStorage.getItem('tdsUserApprovedLocales'); // view list
 * >> localStorage.clear(); // reset
 *
 * CAUTION:
 * Renaming either of these variables will reset visitors' localStorage globally
 */
export const APPROVED_LOCALES_KEY = 'tdsUserApprovedLocales';
export const APPROVED_SINGLE_LOCALE_KEY = 'tdsUserApprovedSingleLocale';
/**
 * useApprovedLocales
 * - Hook provides access to the user's approved locales in browser settings and local storage
 *
 * @param currentLocale locale code of page's locale
 * @returns
 */
export const useApprovedLocales = (currentLocale, isSingleValue = false) => {
    var _a, _b;
    // normalizing the `currentLocale` value passed in
    const parsedCurrentLocale = parseLocale(currentLocale).locale;
    // default locale approval status to false
    let isApprovedLocale = false;
    // If user visits site for a different locale, check if the user has approved/set
    // this locale manually (via some other action)
    if (isSingleValue) {
        // only looking for the `APPROVED_SINGLE_LOCALE_KEY` single value set in local storage
        const approvedLocale = getSingleApprovedLocale();
        // checking if `currentLocale` set and is equal to what is set in `APPROVED_SINGLE_LOCALE_KEY`
        if (approvedLocale !== '' && ((_a = parseLocale(approvedLocale)) === null || _a === void 0 ? void 0 : _a.locale) === parsedCurrentLocale) {
            isApprovedLocale = true;
        }
    }
    else {
        // checking if the `currentLocale` is in the user's browser's list of languages
        // NOTE: the value of navigator.language is always the first value of the languages array
        if (typeof window !== 'undefined') {
            (_b = window.navigator) === null || _b === void 0 ? void 0 : _b.languages.forEach((lang) => {
                if (parseLocale(lang).locale === parsedCurrentLocale) {
                    isApprovedLocale = true;
                }
            });
        }
        // get the list of locales the user has approved manually (via some other action)
        const approvedLocales = getApprovedLocales();
        // evaluating the list of approved locales against `currentLocale`
        approvedLocales.forEach((locale) => {
            if (locale === parsedCurrentLocale) {
                isApprovedLocale = true;
            }
        });
    }
    return {
        approveLocale,
        isApprovedLocale,
    };
};
/**
 * getApprovedLocales
 * @returns array of locales the user has manually set in the site's local storage key
 * NOTE: any new value selected by the user gets appended to the array of site-related locales
 */
export const getApprovedLocales = () => {
    if (typeof localStorage === 'undefined') {
        return [];
    }
    if (localStorage.length > 0) {
        // return an array of the values stored at the key
        return ((localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(APPROVED_LOCALES_KEY)) || '').split(',');
    }
    return [];
};
/**
 * getSingleApprovedLocale
 * @returns single locale most recently set by the user in the site's local storage key
 * NOTE: this value gets overwritten every time it is set
 */
export const getSingleApprovedLocale = () => {
    var _a;
    // if no localStorage (to handle cases like SSR)
    if (typeof localStorage === 'undefined') {
        return '';
    }
    // return the value if it's set
    return (_a = localStorage.getItem(APPROVED_SINGLE_LOCALE_KEY)) !== null && _a !== void 0 ? _a : '';
};
/**
 * approveLocale - setting the approved locale for the user's localstorage
 *
 * @param locale locale code to approve
 * @param isSingleValue boolean to indicate that the localstorage value should reference
 */
export const approveLocale = (locale, isSingleValue = false) => {
    // if we're using a single-value key then we're going to just set it
    if (isSingleValue) {
        localStorage.setItem(APPROVED_SINGLE_LOCALE_KEY, parseLocale(locale).locale);
    }
    else {
        const approvedLocales = getApprovedLocales();
        // if not already in the array of approved, we add the locale as the end of the array
        if (!approvedLocales.includes(locale)) {
            approvedLocales.push(parseLocale(locale).locale);
        }
        // update localstorage with changes
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(APPROVED_LOCALES_KEY, String(approvedLocales));
    }
};
